import { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import authMonitorService from "../services/auth-monitor-service";
import logger from "../helpers/logger";

/**
 * React Hook for Authentication Monitoring
 * Provides authentication status, automatic refresh, and user notifications
 */
export const useAuthMonitor = () => {
  const { instance, accounts } = useMsal();
  const [authStatus, setAuthStatus] = useState({
    status: "initializing",
    message: "Initializing authentication monitor...",
    expiresIn: null,
    requiresAction: false,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const lastUpdateRef = useRef(null);

  // Initialize the auth monitor service
  useEffect(() => {
    if (instance && accounts && accounts.length > 0 && !isInitialized) {
      logger.log("🔐 Initializing authentication monitor hook");

      const currentAccount = accounts[0];
      authMonitorService.initialize(instance, currentAccount);
      setIsInitialized(true);

      // Set up listener for status updates with debouncing
      const unsubscribe = authMonitorService.addListener((status) => {
        // Prevent rapid updates by comparing with last update
        // Round to 5-minute intervals to reduce flashing
        const roundedMinutes = Math.ceil((status.expiresIn || 0) / 5) * 5;
        const statusKey = `${status.status}-${roundedMinutes}`;
        if (lastUpdateRef.current !== statusKey) {
          lastUpdateRef.current = statusKey;
          setAuthStatus(status);
        }
      });

      // Get initial status
      authMonitorService.getCurrentStatus().then((status) => {
        setAuthStatus(status);
      });

      // Cleanup function - only unsubscribe, don't reset initialized state
      return () => {
        logger.log("🧹 Cleaning up auth monitor hook");
        unsubscribe();
        // DON'T call stopMonitoring or setIsInitialized(false) - causes infinite loop
      };
    }
  }, [instance, accounts, isInitialized]);

  // Manual refresh function
  const refreshAuth = useCallback(async () => {
    logger.log("🔄 Manual authentication refresh requested");
    const success = await authMonitorService.manualRefresh();

    if (success) {
      // Get updated status
      const newStatus = await authMonitorService.getCurrentStatus();
      setAuthStatus(newStatus);
    }

    return success;
  }, []);

  // Force status check
  const checkStatus = useCallback(async () => {
    const status = await authMonitorService.getCurrentStatus();
    setAuthStatus(status);
    return status;
  }, []);

  return {
    authStatus,
    isInitialized,
    refreshAuth,
    checkStatus,
    isHealthy: authStatus.status === "healthy",
    isWarning: authStatus.status === "warning",
    isCritical: authStatus.status === "critical",
    isExpired: authStatus.status === "expired",
    requiresAction: authStatus.requiresAction || false,
  };
};
